import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  ORDER_TYPE_ENUM,
  type OrderType,
  type PaymentMethod,
} from "@/constants/enums";
import type {
  CartLine,
  SelectedAddress,
  SelectedTable,
  Staff,
  StaffStamp,
} from "@/types";
import { staffUid } from "./authSlice";
import type { OrderDiscountDetails, WalletRedemptions } from "@/types/wallet";
import type { RootState } from "..";
import { calculateSubtotal } from "@/utils/pricing";

/** `lineId` is derived by the reducer, so callers must not supply one. */
export type AddItemPayload = Omit<CartLine, "lineId"> & { quantity?: number };

interface CartState {
  orderType: OrderType;
  items: CartLine[];
  note: string;
  scheduledFor: string | null;
  table: SelectedTable | null;
  address: SelectedAddress | null;
  paymentMethod: PaymentMethod | null;
  /**
   * `cart.data._id` — the OPEN DINE-IN BILL this cart is adding a round to.
   *
   * The original's checkout branches on exactly this: `postEditOrder` does
   * `if (O._id)` -> `PUT /orders/:tenant/:id`, and reaches `POST /orders` only
   * when there is none. It is adopted from
   * `GET /dinein-tables/:tenant/:table?with_order=true` when the table already
   * has a bill running, and it is what stops a second round from opening a
   * second order — the defect that left order #1 open, uncollectable and
   * detached from the floor plan.
   *
   * `null` for every takeaway and delivery order, and for the FIRST round at a
   * table. It is deliberately cleared whenever the table changes or the cart is
   * emptied: a bill belongs to one table.
   */
  resumedOrderId: string | null;
  /**
   * `cart.data.tip_amount` — the tip, in major currency units, exactly as the
   * original stores it. It is the ONE money field on the cart the customer sets
   * directly; everything else is derived from the catalog and the tenant's
   * settings.
   *
   * It is a number and not a string on purpose: the tip field's own text state
   * belongs to the field (it has to hold `"1."` mid-typing), and the original
   * commits a `parseFloat`-ed number here on blur.
   */
  tipAmount: number;
  /**
   * The wallet's three cart-side fields, recovered from `cart.data`.
   *
   * `redeemed_offers` is `{ [offerId]: claims }` — the ONLY record of a claimed
   * offer in the original; there is no "claimed offers" list anywhere. The
   * offer card reads it for its consumed badge and its Remove control, and the
   * spendable-balance helpers subtract it from the customer's voucher counts.
   *
   * `order_discount_details` and `wallet_redemptions` are what the applied
   * voucher / gift card / cashback land in. Order PRICING is server-authoritative
   * in this reconstruction (a documented deviation), so these carry the
   * identity of what was applied, not a locally computed total.
   */
  redeemed_offers: Record<string, number> | null;
  order_discount_details: OrderDiscountDetails | null;
  wallet_redemptions: WalletRedemptions | null;
  /**
   * `cart.data.order_taker_details` — WHO took this order, set by a member of
   * staff through `ASSIGN_ORDER_TAKER_MODAL` and posted with the order so the
   * POS can attribute it.
   *
   * It is `null` for every customer order and there is no way for a customer to
   * set it: the row that opens the modal is gated on
   * `isStaff && POS_UPDATE_ASSIGN_ORDER_TAKER`.
   */
  order_taker_details: StaffStamp | null;
}

const initialState: CartState = {
  orderType: ORDER_TYPE_ENUM.TAKE_AWAY,
  items: [],
  note: "",
  scheduledFor: null,
  table: null,
  address: null,
  paymentMethod: null,
  resumedOrderId: null,
  tipAmount: 0,
  redeemed_offers: null,
  order_discount_details: null,
  wallet_redemptions: null,
  order_taker_details: null,
};

/**
 * A stable identity for a basket row: the item, its chosen options and its
 * note. Two adds producing the same signature are the same row and merge.
 *
 * Deriving the id here rather than at the call site is what makes duplicates
 * impossible. `ItemOptionsDrawer` previously built the id from the item and
 * its options itself, so adding the same configuration twice created two rows
 * sharing one id — and `removeItem` filters on that id, so deleting one row
 * silently deleted both.
 */
const lineSignature = (line: Omit<CartLine, "lineId">): string => {
  // The chosen QUANTITY is part of the configuration, not just the option id:
  // "extra cheese ×1" and "extra cheese ×2" are two different rows and two
  // different prices, and merging them silently threw the second one's
  // surcharge away. The original's own merge key compares the whole
  // `modifiers` tree, quantities included (`getAddItemToCartData`).
  const modifiers = (line.modifiers ?? [])
    .map((modifier) => `${modifier._id}*${modifier.quantity ?? 1}`)
    .slice()
    .sort()
    .join("+");
  const note = line.note?.trim() ?? "";
  return [line.itemId, modifiers, note].join("|");
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setOrderType(state, action: PayloadAction<OrderType>) {
      state.orderType = action.payload;
    },
    /** Callers pass the line without an id; the reducer derives and dedupes it. */
    addItem(state, action: PayloadAction<AddItemPayload>) {
      const incoming = {
        ...action.payload,
        quantity: action.payload.quantity ?? 1,
        modifiers: action.payload.modifiers ?? [],
      };
      const lineId = lineSignature(incoming);

      const existing = state.items.find((item) => item.lineId === lineId);
      if (existing) {
        existing.quantity += incoming.quantity;
        return;
      }

      state.items.push({ ...incoming, lineId });
    },
    updateItemQuantity(
      state,
      action: PayloadAction<{ lineId: string; quantity: number }>,
    ) {
      const { lineId, quantity } = action.payload;
      const line = state.items.find((item) => item.lineId === lineId);
      if (!line) return;
      if (quantity <= 0) {
        state.items = state.items.filter((item) => item.lineId !== lineId);
      } else {
        line.quantity = quantity;
      }
    },
    removeItem(state, action: PayloadAction<string>) {
      state.items = state.items.filter(
        (item) => item.lineId !== action.payload,
      );
    },
    setNote(state, action: PayloadAction<string>) {
      state.note = action.payload ?? "";
    },
    setScheduledFor(state, action: PayloadAction<string | null>) {
      state.scheduledFor = action.payload ?? null;
    },
    setTable(state, action: PayloadAction<SelectedTable | null>) {
      const next = action.payload ?? null;
      // Moving to a different table abandons the bill this cart had adopted —
      // a round must never be appended to the bill of the table the customer
      // just left. The resume lookup runs again for the new table and re-adopts
      // if that one has a bill of its own.
      if (next?.tableId !== state.table?.tableId) state.resumedOrderId = null;
      state.table = next;
    },

    /**
     * Adopt (or release) the open bill returned by the table-resume lookup.
     * `null` means "this table has no open bill" — the next checkout POSTs a
     * new order, which is the correct behaviour for the first round.
     */
    setResumedOrderId(state, action: PayloadAction<string | null>) {
      state.resumedOrderId = action.payload ?? null;
    },
    setAddress(state, action: PayloadAction<SelectedAddress | null>) {
      state.address = action.payload ?? null;
    },
    /**
     * `setUpdateCartData({ tip_amount })` — the tip block's only write.
     *
     * The clamp is the original's own: its blur handler does
     * `let e = parseFloat(text); let t = !isNaN(e) && e > 0 ? e : 0;` before
     * dispatching, so a blank, a `NaN` and a negative all commit as `0`. The
     * percentage chips post an already-rounded amount.
     */
    setTipAmount(state, action: PayloadAction<number>) {
      const value = Number(action.payload);
      state.tipAmount = Number.isFinite(value) && value > 0 ? value : 0;
    },
    setPaymentMethod(state, action: PayloadAction<PaymentMethod | null>) {
      state.paymentMethod = action.payload ?? null;
    },
    /**
     * Merge a wallet patch into the cart, which is how the original applies
     * every claim: the helpers in `lib/offers.ts` return a partial and the
     * reducer folds it in. `undefined` members are left alone so a patch that
     * only touches one field cannot blank the others.
     */
    applyWalletPatch(
      state,
      action: PayloadAction<{
        redeemed_offers?: Record<string, number> | null;
        order_discount_details?: OrderDiscountDetails | null;
        wallet_redemptions?: WalletRedemptions | null;
      }>,
    ) {
      const patch = action.payload ?? {};
      if ("redeemed_offers" in patch) {
        state.redeemed_offers = patch.redeemed_offers ?? null;
      }
      if ("order_discount_details" in patch) {
        state.order_discount_details = patch.order_discount_details ?? null;
      }
      if ("wallet_redemptions" in patch) {
        state.wallet_redemptions = patch.wallet_redemptions ?? null;
      }
    },
    /**
     * `setAssignOrderTakerToCart` — the cart slice's own reducer (module
     * `21764`), verbatim:
     *
     * ```js
     * order_taker_details: t.payload != null
     *   ? { staff_name: t.payload.display_name ?? "N/A", staff_id: t.payload.uid ?? "",
     *       staff_mobile: t.payload.phone_number ?? null, staff_email: t.payload.email ?? null }
     *   : null
     * ```
     *
     * Including the `?? ""` on `staff_id`, which is the empty string the whole
     * of `mongo-api/src/lib/staffIdentity.js` exists to survive — `staffUid()`
     * makes it a real id here, but the fallback stays because the shape is the
     * shape the POS reads.
     */
    setAssignOrderTakerToCart(state, action: PayloadAction<Staff | null>) {
      const staff = action.payload;
      state.order_taker_details =
        staff != null
          ? {
              staff_name: staff.display_name ?? "N/A",
              staff_id: staffUid(staff) ?? "",
              staff_mobile: staff.phone_number ?? null,
              staff_email: staff.email ?? null,
            }
          : null;
    },
    /** Drop every wallet claim — the original's clear path. */
    clearWalletRedemptions(state) {
      state.redeemed_offers = null;
      state.order_discount_details = null;
      state.wallet_redemptions = null;
    },
    clearCart() {
      return initialState;
    },
  },
});

export const {
  setOrderType,
  addItem,
  updateItemQuantity,
  removeItem,
  setNote,
  setScheduledFor,
  setTable,
  setAddress,
  setPaymentMethod,
  setResumedOrderId,
  setTipAmount,
  setAssignOrderTakerToCart,
  applyWalletPatch,
  clearWalletRedemptions,
  clearCart,
} = cartSlice.actions;

export const selectCartCount = (state: RootState): number =>
  state.cart.items.reduce((sum, item) => sum + item.quantity, 0);

export const selectCartSubtotal = (state: RootState): number =>
  calculateSubtotal(state.cart.items);

/**
 * `cart.data.total_discount_applied` — what the basket has saved off the LIST
 * price, summed per line. The original reads this straight off its cart
 * document; this reconstruction derives it from `listPrice - price`, which are
 * the item's own `price` and `discounted_price` as the catalog serves them.
 *
 * Only the "View Cart" pill (`b`) renders it, and only when it is > 0.
 */
export const selectCartTotalDiscountApplied = (state: RootState): number =>
  state.cart.items.reduce((sum, item) => {
    const list = item.listPrice;
    if (list == null || list <= item.price) return sum;
    return sum + (list - item.price) * item.quantity;
  }, 0);

export default cartSlice.reducer;
